import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Shield, Calendar, CreditCard, AlertCircle, Check } from "lucide-react";
import type { LegacySubscription, BillingHistoryEntry } from "@shared/schema";

interface SubscriptionManagementProps {
  subscription: LegacySubscription | null;
  billingHistory: BillingHistoryEntry[];
  token: string;
  onSubscriptionCancelled?: () => void;
}

function formatDate(date: Date | string | null): string {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatCurrency(amount: number | null): string {
  if (amount === null) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount / 100);
}

function getStatusBadgeVariant(status: string | null): "default" | "secondary" | "destructive" | "outline" {
  switch (status?.toLowerCase()) {
    case "active":
      return "default";
    case "cancelled":
      return "destructive";
    case "paused":
      return "secondary";
    default:
      return "outline";
  }
}

function getBillingStatusBadgeVariant(status: string | null): "default" | "secondary" | "destructive" | "outline" {
  switch (status?.toLowerCase()) {
    case "paid":
      return "default";
    case "failed":
      return "destructive";
    case "pending":
      return "secondary";
    default:
      return "outline";
  }
}

export function SubscriptionManagement({
  subscription,
  billingHistory,
  token,
  onSubscriptionCancelled,
}: SubscriptionManagementProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const cancelMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", `/api/portal/${token}/cancel-subscription`);
    },
    onSuccess: () => {
      toast({
        title: "Subscription Cancelled",
        description: "Your insurance subscription has been cancelled. You can resubscribe at any time.",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/portal/${token}`] });
      setCancelDialogOpen(false);
      onSubscriptionCancelled?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel subscription. Please try again.",
        variant: "destructive",
      });
    },
  });

  if (!subscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Plant Insurance
          </CardTitle>
          <CardDescription>
            Protect your terrarium with our care insurance plan
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/50 p-6 rounded-lg text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground mb-4">
              You don't have an active insurance subscription. Insurance includes:
            </p>
            <ul className="text-sm text-muted-foreground space-y-2 mb-4">
              <li className="flex items-center justify-center gap-2">
                <Check className="w-4 h-4 text-primary" />
                Free plant replacements
              </li>
              <li className="flex items-center justify-center gap-2">
                <Check className="w-4 h-4 text-primary" />
                Annual maintenance visits
              </li>
              <li className="flex items-center justify-center gap-2">
                <Check className="w-4 h-4 text-primary" />
                Priority customer support
              </li>
            </ul>
            <p className="text-sm text-muted-foreground">
              Contact us at hello@lifeitself.studio to add insurance to your order.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isActive = subscription.status === "active";

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <CardTitle className="font-serif text-xl">Plant Insurance</CardTitle>
          </div>
          <Badge variant={getStatusBadgeVariant(subscription.status)}>
            {subscription.status || "Unknown"}
          </Badge>
        </div>
        <CardDescription>
          Keep your terrarium thriving with our care insurance
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Subscription Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm text-muted-foreground">Monthly Amount</p>
              <p className="font-medium">${subscription.monthlyAmount || 20}/month</p>
            </div>
          </div>
          {isActive && subscription.nextBillingDate && (
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Next Billing Date</p>
                <p className="font-medium">{formatDate(subscription.nextBillingDate)}</p>
              </div>
            </div>
          )}
          {subscription.cancelledAt && (
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-destructive mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Cancelled On</p>
                <p className="font-medium">{formatDate(subscription.cancelledAt)}</p>
              </div>
            </div>
          )}
        </div>

        {/* Benefits */}
        {isActive && (
          <>
            <Separator />
            <div>
              <h4 className="font-medium mb-3">Your Benefits</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  Free plant replacements if any die
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  Annual maintenance and refresh visit
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  Priority customer support
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  Care tips and reminders
                </li>
              </ul>
            </div>
          </>
        )}

        {/* Billing History */}
        {billingHistory.length > 0 && (
          <>
            <Separator />
            <div>
              <h4 className="font-medium mb-3">Billing History</h4>
              <div className="space-y-2">
                {billingHistory.slice(0, 5).map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium">{entry.description || "Monthly Insurance"}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(entry.billingDate)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{formatCurrency(entry.amount)}</span>
                      <Badge variant={getBillingStatusBadgeVariant(entry.status)} className="text-xs">
                        {entry.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Cancel Subscription */}
        {isActive && (
          <>
            <Separator />
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">
                  Need to cancel your subscription?
                </p>
              </div>
              <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    Cancel Subscription
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel Insurance Subscription?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to cancel your plant insurance? You'll lose access to:
                      <ul className="mt-2 list-disc list-inside space-y-1">
                        <li>Free plant replacements</li>
                        <li>Annual maintenance visits</li>
                        <li>Priority support</li>
                      </ul>
                      <p className="mt-2">
                        This action cannot be undone, but you can resubscribe at any time.
                      </p>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => cancelMutation.mutate()}
                      disabled={cancelMutation.isPending}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {cancelMutation.isPending ? "Cancelling..." : "Yes, Cancel"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
